import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import { ArrowRight, KeyRound } from "lucide-react";

/**
 * Magic-link landing page. Verification is deliberately gated behind a user
 * tap: corporate email security scanners (Outlook SafeLinks, Mimecast,
 * Proofpoint) pre-open links and were consuming the one-time token before
 * the real user clicked, producing "Link already used" errors.
 */
export default function VerifyPage() {
  const [params] = useSearchParams();
  const { login } = useAuth();
  const nav = useNavigate();
  const token = params.get("token");
  const [status, setStatus] = useState(token ? "ready" : "error"); // ready | verifying | success | error
  const [error, setError] = useState(token ? "" : "Missing token");

  const verify = async () => {
    setStatus("verifying");
    try {
      const { data } = await api.get(`/auth/verify`, { params: { token } });
      login(data.token, data.user);
      setStatus("success");
      const target = data.user.role === "admin" || data.user.role === "tl_internal" ? "/admin" : "/dashboard";
      setTimeout(() => nav(target), 600);
    } catch (e) {
      setStatus("error");
      const raw = e?.response?.data?.detail;
      setError(typeof raw === "string" ? raw : "Verification failed");
    }
  };

  return (
    <Layout>
      <section className="container-summit py-24 text-center max-w-md">
        <div className="overline mb-6"><span className="accent-rule" />Magic Link</div>

        {status === "ready" && (
          <>
            <KeyRound size={26} className="mx-auto text-[#A39171]" strokeWidth={1.4} />
            <h1 className="text-3xl mt-6">You're one tap away.</h1>
            <p className="mt-4 text-zinc-500">
              For your security, confirm below to sign in to your Summit Portal.
            </p>
            <button onClick={verify} className="btn-black mt-8 inline-flex h-12 px-8" data-testid="verify-continue-btn">
              Sign in to my Dashboard <ArrowRight size={14} />
            </button>
          </>
        )}

        {status === "verifying" && <h1 className="text-3xl">Verifying your link…</h1>}
        {status === "success" && <h1 className="text-3xl">Welcome back.</h1>}
        {status === "error" && (
          <>
            <h1 className="text-3xl">We couldn't verify that link.</h1>
            <p className="mt-4 text-zinc-500" data-testid="verify-error">{error}</p>
            <p className="mt-3 text-sm text-zinc-500">
              Magic links expire and can only be used for a short window. Request a fresh
              link below — it only takes a few seconds.
            </p>
            <a href="/login" className="btn-outline-black mt-8 inline-flex" data-testid="verify-retry-link">Request a new link</a>
          </>
        )}
      </section>
    </Layout>
  );
}
